import { Box, Button, Flex, Heading, Input, InputGroup, SimpleGrid, Text } from '@chakra-ui/react';
import { UseFormReturn, useController } from 'react-hook-form';
import { useDebouncedFieldRegistry } from '@/utils/filterUtils/useDebouncedFieldRegistry';
import { ChakraFeatherIcon } from 'src/components/ChakraFeatherIcon';
import { SelectField } from 'src/components/SelectField';
import { CalendarEventsFilters } from '../../types';
import { formatDateToDateInputValue } from '@/utils/formatDateToDateInputValue';
import { InputField } from '@/components/InputField';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Club } from '@/model';
import TeamFilterSelect from '@/components/TeamFilterSelect';

export interface CalendarEventsFilterBoxProps {
  filtersForm: UseFormReturn<CalendarEventsFilters>;
  forceRefetch: () => void;
  clubs: Club[];
}

export const CalendarEventsFilterBox = ({ clubs, filtersForm }: CalendarEventsFilterBoxProps) => {
  const { t } = useTranslation();
  const { register, reset, control, setValue, watch } = filtersForm;
  const [month, setMonth] = useState('');

  const startDateControl = useController({ name: 'start_date', control });
  const endDateControl = useController({ name: 'end_date', control });

  // Watch para detectar mudanças em start_date e end_date
  const startDate = watch('start_date');
  const endDate = watch('end_date');

  // Atualiza start_date e end_date ao mudar o mês
  const handleMonthChange = (selectedMonth: string) => {
    setMonth(selectedMonth);

    if (selectedMonth !== '') {
      const now = new Date();
      const year = now.getFullYear();
      const monthIndex = parseInt(selectedMonth, 10);

      const firstDay = new Date(year, monthIndex, 1);
      const lastDay = new Date(year, monthIndex + 1, 0);

      setValue('start_date', firstDay.toISOString().split('T')[0]);
      setValue('end_date', lastDay.toISOString().split('T')[0]);
    } else {
      setValue('start_date', '');
      setValue('end_date', '');
    }
  };

  const onHandleReset = useCallback(() => {
    setMonth('');
    reset();
  }, [reset]);

  useEffect(() => {
    if (startDate || endDate) {
      let startMonthIndex = -1;
      let endMonthIndex = -1;

      if (startDate) {
        startMonthIndex = parseInt(startDate.split('-')[1].replace(/^0+/, ''), 10);
        if (Number.isNaN(startMonthIndex)) {
          startMonthIndex = -1;
        }
        startMonthIndex = startMonthIndex - 1;
      }

      if (endDate) {
        //get month from end date
        endMonthIndex = parseInt(endDate.split('-')[1].replace(/^0+/, ''), 10);
        if (Number.isNaN(endMonthIndex)) {
          endMonthIndex = -1;
        }
        endMonthIndex = endMonthIndex - 1;
      }

      if (startMonthIndex && endMonthIndex && startMonthIndex !== endMonthIndex) {
        setMonth('');
      }

      if (startMonthIndex && endMonthIndex && startMonthIndex === endMonthIndex) {
        setMonth(startMonthIndex.toString());
      }

      if (startMonthIndex && !endMonthIndex) {
        setMonth(startMonthIndex.toString());
      }

      if (!startMonthIndex && endMonthIndex) {
        setMonth(endMonthIndex.toString());
      }
    }
  }, [startDate, endDate]);

  return (
    <Box>
      <Flex align='center' gap='pico' mb='deka'>
        <ChakraFeatherIcon color='text.main' name='filter' />
        <Heading variant='sectionTitle'>
          {t('pages.application_calendar_events.components.calendar_events_filter_box.title')}
        </Heading>
      </Flex>

      <Flex direction={{ base: 'row' }} gap='small' rowGap='micro'>
        <SimpleGrid
          gap='deka'
          alignItems='flex-end'
          columns={{ base: 1, sm: 2, md: 4, lg: 5, xl: 6 }}
          rowGap='xxsmall'
          flexGrow={1}
        >
          <TeamFilterSelect showFlag name='team_uuid' control={filtersForm.control} clubs={clubs} width='100%' />

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_calendar_events.components.calendar_events_filter_box.event_name')}
            </Text>
            <Input variant='flushed' {...useDebouncedFieldRegistry(() => register('name'))} />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>{t('pages.application_calendar_events.components.calendar_events_filter_box.month')}</Text>
            <SelectField onChange={e => handleMonthChange(e.currentTarget.value)} value={month}>
              <option value=''>{t('pages.application_calendar_events.components.calendar_events_filter_box.all')}</option>
              <option value='0'>{t('pages.application_calendar_events.components.calendar_events_filter_box.january')}</option>
              <option value='1'>{t('pages.application_calendar_events.components.calendar_events_filter_box.february')}</option>
              <option value='2'>{t('pages.application_calendar_events.components.calendar_events_filter_box.march')}</option>
              <option value='3'>{t('pages.application_calendar_events.components.calendar_events_filter_box.april')}</option>
              <option value='4'>{t('pages.application_calendar_events.components.calendar_events_filter_box.may')}</option>
              <option value='5'>{t('pages.application_calendar_events.components.calendar_events_filter_box.june')}</option>
              <option value='6'>{t('pages.application_calendar_events.components.calendar_events_filter_box.july')}</option>
              <option value='7'>{t('pages.application_calendar_events.components.calendar_events_filter_box.august')}</option>
              <option value='8'>{t('pages.application_calendar_events.components.calendar_events_filter_box.september')}</option>
              <option value='9'>{t('pages.application_calendar_events.components.calendar_events_filter_box.october')}</option>
              <option value='10'>{t('pages.application_calendar_events.components.calendar_events_filter_box.november')}</option>
              <option value='11'>{t('pages.application_calendar_events.components.calendar_events_filter_box.december')}</option>
            </SelectField>
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_calendar_events.components.calendar_events_filter_box.start_date')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(startDateControl.field.value)}
              onChange={event => startDateControl.field.onChange(event.target.value)}
            />
          </InputGroup>

          <InputGroup display='flex' flexDirection='column'>
            <Text fontWeight='medium'>
              {t('pages.application_calendar_events.components.calendar_events_filter_box.end_date')}
            </Text>
            <InputField
              type='date'
              fullDatePickerArea
              variant='flushed'
              value={formatDateToDateInputValue(endDateControl.field.value)}
              onChange={event => endDateControl.field.onChange(event.target.value)}
              min={formatDateToDateInputValue(startDateControl.field.value)}
            />
          </InputGroup>
        </SimpleGrid>

        <Button alignSelf={{ lg: 'end' }} colorScheme='text' variant='outline' mt='10px' onClick={() => onHandleReset()}>
          {t('pages.application_calendar_events.components.calendar_events_filter_box.clear_filters')}
        </Button>
      </Flex>
    </Box>
  );
};
